// The iframe side of the dashboard handshake, without React. The provider wraps one instance.
//
// Security rules (see CONTRIBUTING.md):
// - only messages whose origin is allow-listed AND whose source is our parent window are read;
// - we only ever post to allow-listed origins, never '*';
// - the session token lives in this object's memory, never in storage or the URL.

import {
  APP_CONTEXT_EVENT,
  APP_ROUTE_EVENT,
  APP_TOKEN_EVENT,
  type AppContextPayload,
  FETCH_INFO_MESSAGE,
  HOST_ACTIONS,
  type HostAccount,
  type HostAction,
  type HostAgent,
  type HostContact,
  type HostConversation,
  INSERT_REPLY_MAX_LENGTH,
  isAppPath,
  type NavigateTarget,
  parseDashboardMessage,
  type Theme,
  TOAST_MAX_LENGTH,
} from '../protocol.ts';
import { tokenExpiresAt } from './jwt.ts';

export type HostStatus = 'loading' | 'ready' | 'standalone' | 'error';

export interface HostState {
  status: HostStatus;
  /** Why status is `error`. */
  error: string | null;
  account: HostAccount | null;
  currentAgent: HostAgent | null;
  /** Only in conversation tabs. `id` is the display id. */
  conversation: HostConversation | null;
  contact: HostContact | null;
  theme: Theme;
  /** Origin of the dashboard that framed us, once known. */
  hostOrigin: string | null;
  /** True when a session token is available (embedded, or the dev fallback). */
  hasToken: boolean;
}

/** Local development outside the dashboard: where to get a token and what context to pretend. */
export interface DevFallback {
  getToken: () => Promise<string>;
  context?: Partial<
    Pick<AppContextPayload, 'account' | 'currentAgent' | 'conversation' | 'contact' | 'theme'>
  >;
}

/** The slice of `window` the bridge uses; injectable for tests. */
export interface BridgeWindow {
  parent: { postMessage(message: unknown, targetOrigin: string): void } | null;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

export interface HostBridgeOptions {
  /** Exact dashboard origins, e.g. ['https://chat.w3layouts.com']. */
  allowedOrigins: readonly string[];
  /** Used only when not framed AND `isDev` is true. */
  devFallback?: DevFallback | undefined;
  /** Whether this is a development build. Default: `import.meta.env.DEV`. */
  isDev?: boolean;
  /** Toggle the `dark` class on <html> to follow the dashboard theme. Default true. */
  syncTheme?: boolean;
  /** Called for host actions when running standalone (no dashboard to handle them). */
  onStandaloneAction?: (action: HostAction) => void;
  handshakeTimeoutMs?: number;
  refreshTimeoutMs?: number;
  /** Testing seam; defaults to the real window. */
  window?: BridgeWindow;
}

export class HostError extends Error {
  readonly code: 'no_session' | 'refresh_failed' | 'refresh_timeout';

  constructor(code: HostError['code'], message: string) {
    super(message);
    this.name = 'HostError';
    this.code = code;
  }
}

const INITIAL_STATE: HostState = {
  status: 'loading',
  error: null,
  account: null,
  currentAgent: null,
  conversation: null,
  contact: null,
  theme: 'light',
  hostOrigin: null,
  hasToken: false,
};

const RETRY_FETCH_INFO_AFTER_MS = [1_500, 4_000];

const defaultIsDev = (): boolean => {
  try {
    return import.meta.env.DEV === true;
  } catch {
    return false;
  }
};

export function applyThemeClass(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

export class HostBridge {
  readonly #options: HostBridgeOptions;
  readonly #allowed: Set<string>;
  #win: BridgeWindow | null = null;
  #state: HostState = INITIAL_STATE;
  #token: string | null = null;
  #listeners = new Set<() => void>();
  #timers: ReturnType<typeof setTimeout>[] = [];
  #refreshing: Promise<string> | null = null;
  #pendingRefresh: { resolve: (token: string) => void; reject: (error: Error) => void } | null = null;
  #started = false;

  constructor(options: HostBridgeOptions) {
    this.#options = options;
    this.#allowed = new Set(options.allowedOrigins.map((origin) => origin.replace(/\/+$/, '').toLowerCase()));
  }

  // ---- store (useSyncExternalStore) ------------------------------------------------------------

  getState = (): HostState => this.#state;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  #setState(patch: Partial<HostState>): void {
    this.#state = { ...this.#state, ...patch, hasToken: this.#token !== null };
    for (const listener of this.#listeners) listener();
  }

  // ---- lifecycle -------------------------------------------------------------------------------

  start(): void {
    if (this.#started) return;
    this.#started = true;
    const win: BridgeWindow | null = this.#options.window ?? (typeof window === 'undefined' ? null : window);
    this.#win = win;
    if (!win) return;

    if (!this.#isFramed(win)) {
      void this.#startStandalone();
      return;
    }
    win.addEventListener('message', this.#onMessage);
    this.#requestContext();
    for (const delay of RETRY_FETCH_INFO_AFTER_MS) {
      this.#timers.push(
        setTimeout(() => {
          if (this.#state.status === 'loading') this.#requestContext();
        }, delay),
      );
    }
    this.#timers.push(
      setTimeout(() => {
        if (this.#state.status === 'loading') {
          this.#setState({ status: 'error', error: 'The dashboard did not answer. Reload the page.' });
        }
      }, this.#options.handshakeTimeoutMs ?? 10_000),
    );
  }

  stop(): void {
    if (!this.#started) return;
    this.#started = false;
    this.#win?.removeEventListener('message', this.#onMessage);
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers = [];
    this.#pendingRefresh?.reject(new HostError('refresh_failed', 'Host bridge stopped'));
    this.#pendingRefresh = null;
  }

  #isFramed(win: BridgeWindow): boolean {
    return win.parent !== null && (win.parent as unknown) !== win;
  }

  #requestContext(): void {
    // The parent's origin is not known yet: address each allowed origin; the browser drops the
    // copies whose origin does not match the parent, so nothing leaks to a foreign embedder.
    for (const origin of this.#allowed) this.#win?.parent?.postMessage(FETCH_INFO_MESSAGE, origin);
  }

  async #startStandalone(): Promise<void> {
    const fallback = this.#options.devFallback;
    const isDev = this.#options.isDev ?? defaultIsDev();
    const context = fallback?.context ?? {};
    const theme = context.theme ?? 'light';
    if (this.#options.syncTheme !== false) applyThemeClass(theme);
    if (!fallback || !isDev) {
      this.#setState({ status: 'standalone', theme });
      return;
    }
    try {
      this.#token = await fallback.getToken();
      this.#setState({
        status: 'standalone',
        theme,
        account: context.account ?? null,
        currentAgent: context.currentAgent ?? null,
        conversation: context.conversation ?? null,
        contact: context.contact ?? null,
      });
    } catch (error) {
      this.#setState({
        status: 'error',
        error: `Dev fallback could not get a token: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  // ---- incoming messages -----------------------------------------------------------------------

  #onMessage = (event: MessageEvent): void => {
    this.handleMessage(event);
  };

  /** Processes one message event. Public for tests; normally fed by the window listener. */
  handleMessage(event: Pick<MessageEvent, 'data' | 'origin' | 'source'>): void {
    const win = this.#win;
    if (!win || !this.#isFramed(win)) return;
    if (!this.#allowed.has(String(event.origin).toLowerCase())) return;
    if (event.source !== (win.parent as unknown)) return;

    const message = parseDashboardMessage(event.data);
    if (!message) return;

    if (message.event === APP_TOKEN_EVENT) {
      const pending = this.#pendingRefresh;
      this.#pendingRefresh = null;
      if (message.data.appToken) {
        this.#token = message.data.appToken;
        this.#setState({});
        pending?.resolve(message.data.appToken);
      } else {
        pending?.reject(new HostError('refresh_failed', 'The dashboard could not issue a new session token'));
      }
      return;
    }

    if (message.event === APP_ROUTE_EVENT) {
      this.#deliverRoute(message.data.path);
      return;
    }

    if (message.event === APP_CONTEXT_EVENT) this.#applyContext(message.data, event.origin);
  }

  // ---- routes (w3send-app:route) ----------------------------------------------------------------

  #routeListeners = new Set<(path: string) => void>();
  /** A route that arrived before anyone listened (e.g. before the router mounted). */
  #pendingRoute: string | null = null;

  #deliverRoute(path: string): void {
    if (this.#routeListeners.size === 0) {
      this.#pendingRoute = path;
      return;
    }
    for (const listener of this.#routeListeners) listener(path);
  }

  /**
   * Calls `listener` with every page the dashboard asks this frame to show
   * (`w3send-app:route`, after another frame of the app sent `open-app`). A route that arrived
   * before the first listener is delivered to it. Returns an unsubscribe function.
   */
  onRoute = (listener: (path: string) => void): (() => void) => {
    this.#routeListeners.add(listener);
    const pending = this.#pendingRoute;
    if (pending !== null) {
      this.#pendingRoute = null;
      listener(pending);
    }
    return () => this.#routeListeners.delete(listener);
  };

  #applyContext(context: AppContextPayload, origin: string): void {
    if (this.#options.syncTheme !== false) applyThemeClass(context.theme);
    const base = {
      account: context.account,
      currentAgent: context.currentAgent,
      conversation: context.conversation ?? null,
      contact: context.contact ?? null,
      theme: context.theme,
      hostOrigin: origin,
    };
    if (!context.appToken) {
      this.#token = null;
      this.#setState({
        ...base,
        status: 'error',
        error: 'The dashboard could not start a session for this app.',
      });
      return;
    }
    this.#token = context.appToken;
    // A context that arrives while a refresh is pending also answers it.
    const pending = this.#pendingRefresh;
    this.#pendingRefresh = null;
    pending?.resolve(context.appToken);
    this.#setState({ ...base, status: 'ready', error: null });
  }

  // ---- tokens ----------------------------------------------------------------------------------

  /** The current session token, if any. Keep it in memory; never persist it. */
  getToken(): string | null {
    return this.#token;
  }

  /** The current token, refreshed first when it expires within `minValiditySeconds`. */
  async getFreshToken(minValiditySeconds = 30): Promise<string> {
    const token = this.#token;
    if (token) {
      const expiresAt = tokenExpiresAt(token);
      if (expiresAt === null || expiresAt - Date.now() / 1000 > minValiditySeconds) return token;
    }
    if (!token && this.#state.status !== 'ready' && this.#state.status !== 'standalone') {
      throw new HostError('no_session', 'No session yet: the dashboard has not sent a token');
    }
    return this.refreshToken();
  }

  /** Asks the dashboard (or the dev fallback) for a new token. Concurrent calls share one request. */
  refreshToken(): Promise<string> {
    this.#refreshing ??= this.#doRefresh().finally(() => {
      this.#refreshing = null;
    });
    return this.#refreshing;
  }

  async #doRefresh(): Promise<string> {
    const win = this.#win;
    if (!win || !this.#isFramed(win)) {
      const fallback = this.#options.devFallback;
      if (!fallback || !(this.#options.isDev ?? defaultIsDev())) {
        throw new HostError('no_session', 'Not running inside the dashboard');
      }
      this.#token = await fallback.getToken();
      this.#setState({});
      return this.#token;
    }
    const origin = this.#state.hostOrigin;
    if (!origin) throw new HostError('no_session', 'No session yet: the dashboard has not sent a token');

    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.#pendingRefresh?.resolve === resolveOnce) this.#pendingRefresh = null;
        reject(new HostError('refresh_timeout', 'The dashboard did not send a new session token'));
      }, this.#options.refreshTimeoutMs ?? 8_000);
      const resolveOnce = (token: string) => {
        clearTimeout(timer);
        resolve(token);
      };
      this.#pendingRefresh = {
        resolve: resolveOnce,
        reject: (error) => {
          clearTimeout(timer);
          reject(error);
        },
      };
      win.parent?.postMessage({ type: HOST_ACTIONS.REFRESH_TOKEN }, origin);
    });
  }

  // ---- host actions ----------------------------------------------------------------------------

  #post(action: HostAction): void {
    const origin = this.#state.hostOrigin;
    const win = this.#win;
    if (win && this.#isFramed(win) && origin) {
      win.parent?.postMessage(action, origin);
      return;
    }
    if (this.#options.onStandaloneAction) this.#options.onStandaloneAction(action);
    else console.info('[w3send host action, standalone]', action);
  }

  /** Opens a conversation (display id) or contact in the dashboard. */
  navigate(target: NavigateTarget, id: number): void {
    if (target !== 'conversation' && target !== 'contact')
      throw new TypeError('target must be conversation or contact');
    if (!Number.isInteger(id) || id <= 0) throw new RangeError('id must be a positive integer');
    this.#post({ type: HOST_ACTIONS.NAVIGATE, target, id });
  }

  /** Shows a dashboard toast (at most 300 characters). */
  toast(message: string): void {
    const text = String(message).trim().slice(0, TOAST_MAX_LENGTH);
    if (text) this.#post({ type: HOST_ACTIONS.TOAST, message: text });
  }

  /**
   * Opens this app's own sidebar page at `path` (e.g. `/deals/42`) - from a conversation tab. The
   * sidebar frame receives the path as a route event (`onRoute`). Forks before app-platform-2
   * ignore the action.
   */
  openApp(path: string): void {
    if (!isAppPath(path)) throw new TypeError('openApp takes a path inside the app, starting with /');
    this.#post({ type: HOST_ACTIONS.OPEN_APP, path });
  }

  /**
   * Puts `text` into the reply box of the open conversation for the agent to review and send
   * (conversation tabs only; at most 4096 characters). Forks before app-platform-2 ignore it.
   */
  insertReply(text: string): void {
    const trimmed = String(text).trim();
    if (!trimmed) return;
    if (trimmed.length > INSERT_REPLY_MAX_LENGTH)
      throw new RangeError(`insertReply takes at most ${INSERT_REPLY_MAX_LENGTH} characters`);
    this.#post({ type: HOST_ACTIONS.INSERT_REPLY, text: trimmed });
  }

  /** Opens an https URL in a new tab from the dashboard. */
  openUrl(url: string): void {
    if (!/^https:\/\//.test(url)) throw new TypeError('openUrl only accepts https:// URLs');
    this.#post({ type: HOST_ACTIONS.OPEN_URL, url });
  }
}

/**
 * What the provider and `createApiFetch` need from a bridge: HostBridge (W3Chat) and
 * ChatwootBridge (a stock Chatwoot-based CRM) both provide it.
 */
export type AppBridge = Pick<
  HostBridge,
  | 'getState'
  | 'subscribe'
  | 'start'
  | 'stop'
  | 'getToken'
  | 'getFreshToken'
  | 'refreshToken'
  | 'onRoute'
  | 'navigate'
  | 'toast'
  | 'openApp'
  | 'insertReply'
  | 'openUrl'
>;
